/**
 * View showing available data sources for an entity level
 */

import { AlertCircle, Database } from 'lucide-react'
import { useDataSources } from '../hooks/queries'
import type { EntityLevel } from '../types'
import { ENTITY_LEVEL_LABELS } from '../types'
import { DataSourceCard } from './DataSourceCard'

interface DataSourcesViewProps {
  entityLevel: EntityLevel
}

export function DataSourcesView({ entityLevel }: DataSourcesViewProps) {
  const { data, isLoading, error } = useDataSources(entityLevel)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p>Fehler beim Laden der Datenquellen</p>
      </div>
    )
  }

  const sources = data?.sources || []

  if (sources.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <Database className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          Keine Datenquellen
        </h3>
        <p className="text-gray-500">
          Für {ENTITY_LEVEL_LABELS[entityLevel]} sind keine zusätzlichen Datenquellen verfügbar.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {sources.length} Datenquelle{sources.length !== 1 ? 'n' : ''} für {ENTITY_LEVEL_LABELS[entityLevel]}
        </p>
      </div>

      <div className="space-y-3">
        {sources.map((source) => (
          <DataSourceCard key={source.table_name} source={source} />
        ))}
      </div>
    </div>
  )
}
